import { QueryClient } from '@tanstack/react-query';
import { fetchAccountInfo, getAccountInfoQueryKey } from '@/services/accountService';
import { fetchOrders, getOrdersQueryKey } from '@/services/orderService';
import { logger } from '@/services/logger';

// プリフェッチの実行状態を管理するMap
const prefetchPromises = new Map<string, Promise<void>>();

/**
 * ログイン後にアカウント情報と注文履歴をプリフェッチするユーティリティ関数
 * 
 * @param queryClient - TanStack QueryのQueryClientインスタンス
 * @param userData - ログインしたユーザーのデータ
 */
export const prefetchUserData = async (
  queryClient: QueryClient,
  userData: {
    erpContactId?: number;
    id: string;
    isDemoUser?: boolean;
  }
): Promise<void> => {
  // 既に実行中のプリフェッチがある場合はそれを返す
  const prefetchKey = `prefetch-${userData.id}`;
  const existingPromise = prefetchPromises.get(prefetchKey);
  if (existingPromise) {
    logger.debug('Returning existing prefetch promise');
    return existingPromise;
  }

  // 新しいプリフェッチ処理を作成
  const prefetchPromise = performPrefetch(queryClient, userData)
    .finally(() => {
      // 完了後にMapから削除
      prefetchPromises.delete(prefetchKey);
    });

  // Mapに保存して重複実行を防ぐ
  prefetchPromises.set(prefetchKey, prefetchPromise);
  return prefetchPromise;
};

const performPrefetch = async (
  queryClient: QueryClient,
  userData: {
    erpContactId?: number;
    id: string;
    isDemoUser?: boolean;
  }
) => {
  const promises: Promise<void>[] = [];
  
  // アカウント情報のプリフェッチ
  if (userData.erpContactId) {
    logger.debug('Prefetching account info');
    const accountQueryKey = getAccountInfoQueryKey(userData.erpContactId);
    
    promises.push(
      queryClient.prefetchQuery({
        queryKey: accountQueryKey,
        queryFn: () => fetchAccountInfo(userData.erpContactId!),
        staleTime: Infinity, // 長期間フレッシュな状態を維持
      })
    );
  }
  
  // 注文履歴の最初のページをプリフェッチ
  logger.debug('Prefetching order history');
  const orderQueryKey = getOrdersQueryKey(
    userData.id,
    userData.erpContactId,
    1, // page
    10, // itemsPerPage
    '', // search
    userData.isDemoUser || false
  );
  
  promises.push(
    queryClient.prefetchQuery({
      queryKey: orderQueryKey,
      queryFn: () => fetchOrders({
        userId: userData.id,
        contactId: userData.erpContactId,
        page: 1,
        itemsPerPage: 10,
        search: '',
        isDemoUser: userData.isDemoUser || false
      }),
      staleTime: 5 * 60 * 1000, // 5分間フレッシュ
    })
  );
  
  // 全てのプリフェッチを並列実行
  try {
    await Promise.all(promises);
    logger.debug('User data prefetch completed successfully');
  } catch (error) {
    logger.error('Error during user data prefetch', error);
    // エラーが発生してもログイン処理は継続する
    throw error; // エラーを再スローして呼び出し元で処理
  }
};

/**
 * プリフェッチが必要かチェックして実行するユーティリティ関数
 * すでにキャッシュがある場合はプリフェッチをスキップ
 */
export const checkAndPrefetchUserData = async (
  queryClient: QueryClient,
  userData: {
    erpContactId?: number;
    id: string;
    isDemoUser?: boolean;
  }
): Promise<void> => {
  // アカウント情報がキャッシュされているかチェック
  const accountCached = userData.erpContactId ? 
    queryClient.getQueryState(getAccountInfoQueryKey(userData.erpContactId))?.data !== undefined : 
    true;

  // 注文履歴がキャッシュされているかチェック  
  const orderQueryKey = getOrdersQueryKey(
    userData.id,
    userData.erpContactId,
    1,
    10,
    '',
    userData.isDemoUser || false
  );
  const ordersCached = queryClient.getQueryState(orderQueryKey)?.data !== undefined;

  // 両方キャッシュされている場合はスキップ
  if (accountCached && ordersCached) {
    logger.debug('User data already cached, skipping prefetch');
    return;
  }

  // プリフェッチを実行
  return prefetchUserData(queryClient, userData);
};